import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Card,
  Modal,
  Button,
  Form,
  Badge,
  Row,
  Col,
  Spinner,
} from 'react-bootstrap';
import api from '../api';

function CalendarSettings() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);

  const [title, setTitle] = useState('');
  const [selectedHour, setSelectedHour] = useState(9);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState('AM');

  const [notifyBefore, setNotifyBefore] = useState(5);
  const [recurrence, setRecurrence] = useState('none');
  const [saving, setSaving] = useState(false);

  const isDark =
    document.documentElement.getAttribute('data-theme') === 'dark';

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const reminderOptions = [
    { label: 'None', value: 0 },
    { label: '5 min', value: 5 },
    { label: '10 min', value: 10 },
    { label: '15 min', value: 15 },
    { label: '30 min', value: 30 },
    { label: '1 hour', value: 60 },
    { label: '2 hours', value: 120 },
    { label: '6 hours', value: 360 },
    { label: '12 hours', value: 720 },
    { label: '1 day', value: 1440 },
    { label: '2 days', value: 2880 },
    { label: '1 week', value: 10080 },
  ];

  const repeatOptions = [
    { label: 'No Repeat', value: 'none' },
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Yearly', value: 'yearly' },
  ];

  const hours = Array.from({ length: 12 }, (_, index) => index + 1);
  const minutes = Array.from({ length: 12 }, (_, index) => index * 5);

  useEffect(() => {
    fetchEvents();
  }, [currentDate]);

  const fetchEvents = async () => {
    try {
      setLoading(true);

      const res = await api.get('/calendar', {
        params: {
          month: currentDate.getMonth(),
          year: currentDate.getFullYear(),
        },
      });

      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Calendar fetch failed:', err);
      alert('Failed to load calendar events.');
    } finally {
      setLoading(false);
    }
  };

  const daysInMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  const startDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const formatEastern = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  const formatFullDate = (date) => {
    if (!date) return '';

    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const formatShortDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const normalizeDateOnly = (date) => {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  };

  const sameCalendarDay = (dateA, dateB) => {
    return normalizeDateOnly(dateA) === normalizeDateOnly(dateB);
  };

  const isTodayDate = (date) => {
    const today = new Date();
    return sameCalendarDay(date, today);
  };

  const getEventsForDate = (date) => {
    return events
      .filter((event) => {
        const eventDate = new Date(event.start_time);
        return sameCalendarDay(eventDate, date);
      })
      .sort(
        (a, b) =>
          new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      );
  };

  const calendarDays = useMemo(() => {
    const totalDays = daysInMonth(currentDate);
    const offset = startDay(currentDate);
    const list = [];

    for (let i = 0; i < offset; i++) {
      list.push({
        type: 'empty',
        key: `empty-${i}`,
      });
    }

    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      );

      list.push({
        type: 'day',
        key: `day-${day}`,
        day,
        date,
        events: getEventsForDate(date),
      });
    }

    const remainder = list.length % 7;
    if (remainder !== 0) {
      const blanksNeeded = 7 - remainder;

      for (let i = 0; i < blanksNeeded; i++) {
        list.push({
          type: 'empty',
          key: `tail-empty-${i}`,
        });
      }
    }

    return list;
  }, [currentDate, events]);

  const upcomingEvents = useMemo(() => {
    const now = new Date().getTime();

    return events
      .filter((event) => new Date(event.start_time).getTime() >= now)
      .sort(
        (a, b) =>
          new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      )
      .slice(0, 5);
  }, [events]);

  const handleDayClick = (dayItem) => {
    if (!dayItem?.date) return;

    const date = dayItem.date;
    const dayEvents = getEventsForDate(date);

    setSelectedDate(date);
    setSelectedDayEvents(dayEvents);

    const now = new Date();

    if (sameCalendarDay(date, now)) {
      let hour = now.getHours();
      let minute = Math.ceil(now.getMinutes() / 5) * 5;

      if (minute >= 60) {
        hour += 1;
        minute = 0;
      }

      setSelectedPeriod(hour >= 12 ? 'PM' : 'AM');
      setSelectedHour(hour % 12 === 0 ? 12 : hour % 12);
      setSelectedMinute(minute);
    } else {
      setSelectedHour(9);
      setSelectedMinute(0);
      setSelectedPeriod('AM');
    }

    setShowModal(true);
  };

  const get24Hour = () => {
    if (selectedPeriod === 'AM') {
      return selectedHour === 12 ? 0 : selectedHour;
    }

    return selectedHour === 12 ? 12 : selectedHour + 12;
  };

  const selectedTimeLabel = () => {
    return `${selectedHour}:${String(selectedMinute).padStart(2, '0')} ${selectedPeriod}`;
  };

  const resetForm = () => {
    setTitle('');
    setSelectedHour(9);
    setSelectedMinute(0);
    setSelectedPeriod('AM');
    setNotifyBefore(5);
    setRecurrence('none');
  };

  const refreshSelectedDayEvents = () => {
    if (!selectedDate) return;
    setSelectedDayEvents(getEventsForDate(selectedDate));
  };

  const handleSave = async () => {
    if (!title.trim() || !selectedDate) {
      alert('Please enter an event title.');
      return;
    }

    const fullDate = new Date(selectedDate);
    fullDate.setHours(get24Hour());
    fullDate.setMinutes(selectedMinute);
    fullDate.setSeconds(0);
    fullDate.setMilliseconds(0);

    try {
      setSaving(true);

      await api.post('/calendar', {
        title: title.trim(),
        start_time: fullDate,
        notify_before: parseInt(notifyBefore, 10) || 0,
        recurrence_type: recurrence,
        recurrence_interval: 1,
      });

      resetForm();
      await fetchEvents();

      setTimeout(() => {
        refreshSelectedDayEvents();
      }, 100);
    } catch (err) {
      console.error('Save failed:', err);
      alert('Failed to save event.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (event) => {
    if (!window.confirm(`Delete "${event.title}"?`)) return;

    try {
      await api.delete(`/calendar/${event.id}`);

      setEvents((prev) => prev.filter((item) => item.id !== event.id));
      setSelectedDayEvents((prev) => prev.filter((item) => item.id !== event.id));
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete event.');
    }
  };

  const goToPreviousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const theme = {
    pageBg: isDark ? '#0B0F19' : '#f4f7fb',
    cardBg: isDark ? '#111827' : '#ffffff',
    cardBgSoft: isDark ? '#0F172A' : '#f8fafc',
    border: isDark ? '#1f2937' : '#dbe3ef',
    text: isDark ? '#E5E7EB' : '#111827',
    muted: isDark ? '#9CA3AF' : '#64748b',
    blue: '#2563eb',
    blueSoft: isDark ? '#172554' : '#dbeafe',
    green: '#16a34a',
    red: '#dc2626',
    purple: '#7c3aed',
  };

  const renderDayBox = (item) => {
    if (item.type === 'empty') {
      return (
        <div
          key={item.key}
          style={{
            minHeight: 118,
            background: isDark ? '#0B0F19' : '#f1f5f9',
            borderRight: `1px solid ${theme.border}`,
            borderTop: `1px solid ${theme.border}`,
          }}
        />
      );
    }

    const previewEvents = item.events.slice(0, 2);
    const remaining = item.events.length - previewEvents.length;
    const today = isTodayDate(item.date);

    return (
      <div
        key={item.key}
        onClick={() => handleDayClick(item)}
        style={{
          minHeight: 118,
          padding: 8,
          cursor: 'pointer',
          background: today ? theme.blueSoft : theme.cardBgSoft,
          borderRight: `1px solid ${theme.border}`,
          borderTop: `1px solid ${theme.border}`,
          transition: 'all 0.15s ease',
          overflow: 'hidden',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.01)';
          e.currentTarget.style.zIndex = '2';
          e.currentTarget.style.boxShadow = isDark
            ? '0 10px 25px rgba(0,0,0,0.35)'
            : '0 10px 25px rgba(15,23,42,0.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.zIndex = '1';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div
            style={{
              fontWeight: 900,
              color: today ? '#60a5fa' : theme.text,
              fontSize: 15,
            }}
          >
            {item.day}
          </div>

          {item.events.length > 0 && (
            <Badge bg="primary" pill>
              {item.events.length}
            </Badge>
          )}
        </div>

        {previewEvents.map((event) => (
          <div
            key={`${event.id}-${event.start_time}`}
            style={{
              fontSize: 11,
              background: theme.blue,
              color: '#fff',
              padding: '4px 6px',
              borderRadius: 8,
              marginTop: 4,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: 700,
            }}
            title={`${event.title} (${formatEastern(new Date(event.start_time))})`}
          >
            {formatEastern(new Date(event.start_time))} · {event.title}
            {event.recurrence_type && event.recurrence_type !== 'none'
              ? ' 🔁'
              : ''}
          </div>
        ))}

        {remaining > 0 && (
          <div
            style={{
              color: theme.muted,
              fontSize: 11,
              marginTop: 5,
              fontWeight: 700,
            }}
          >
            +{remaining} more
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: theme.pageBg,
        color: theme.text,
        paddingTop: 24,
        paddingBottom: 40,
      }}
    >
      <Container fluid="lg">
        <div className="d-flex justify-content-between align-items-start mb-4">
          <div>
            <h2 style={{ fontWeight: 900, marginBottom: 4 }}>
              📅 Calendar
            </h2>
            <div style={{ color: theme.muted, fontWeight: 600 }}>
              Eastern Time reminders for streams, orders, restocks, and tasks.
            </div>
          </div>

          <Button variant="outline-primary" onClick={goToToday}>
            Today
          </Button>
        </div>

        <Row className="g-3 mb-3">
          <Col md={8}>
            <Card
              className="shadow-sm"
              style={{
                background: theme.cardBg,
                borderColor: theme.border,
                borderRadius: 18,
              }}
            >
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <Button variant="primary" onClick={goToPreviousMonth}>
                    ‹
                  </Button>

                  <div className="text-center">
                    <h4 style={{ margin: 0, fontWeight: 900, color: theme.text }}>
                      {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h4>
                    <div style={{ color: theme.muted, fontSize: 13, fontWeight: 700 }}>
                      {events.length} event{events.length === 1 ? '' : 's'} this month
                    </div>
                  </div>

                  <Button variant="primary" onClick={goToNextMonth}>
                    ›
                  </Button>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    border: `1px solid ${theme.border}`,
                    borderRadius: 14,
                    overflow: 'hidden',
                  }}
                >
                  {weekDays.map((day) => (
                    <div
                      key={day}
                      style={{
                        background: isDark ? '#0F172A' : '#eef4ff',
                        color: theme.muted,
                        padding: '10px 6px',
                        textAlign: 'center',
                        fontWeight: 900,
                        fontSize: 13,
                        borderRight: `1px solid ${theme.border}`,
                      }}
                    >
                      {day}
                    </div>
                  ))}

                  {loading ? (
                    <div
                      style={{
                        gridColumn: '1 / span 7',
                        padding: 40,
                        textAlign: 'center',
                      }}
                    >
                      <Spinner animation="border" variant="primary" />
                      <div style={{ color: theme.muted, marginTop: 10 }}>
                        Loading calendar...
                      </div>
                    </div>
                  ) : (
                    calendarDays.map(renderDayBox)
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card
              className="shadow-sm mb-3"
              style={{
                background: theme.cardBg,
                borderColor: theme.border,
                borderRadius: 18,
              }}
            >
              <Card.Body>
                <h5 style={{ fontWeight: 900, color: theme.text }}>
                  Upcoming
                </h5>

                {upcomingEvents.length === 0 ? (
                  <div style={{ color: theme.muted, fontWeight: 600 }}>
                    No upcoming events this month.
                  </div>
                ) : (
                  upcomingEvents.map((event) => (
                    <div
                      key={`${event.id}-${event.start_time}`}
                      style={{
                        background: theme.cardBgSoft,
                        border: `1px solid ${theme.border}`,
                        borderRadius: 12,
                        padding: 10,
                        marginTop: 10,
                      }}
                    >
                      <div style={{ color: '#60a5fa', fontWeight: 900 }}>
                        {formatShortDate(new Date(event.start_time))} ·{' '}
                        {formatEastern(new Date(event.start_time))}
                      </div>
                      <div style={{ color: theme.text, fontWeight: 800 }}>
                        {event.title}
                        {event.recurrence_type && event.recurrence_type !== 'none'
                          ? ' 🔁'
                          : ''}
                      </div>
                      <div style={{ color: theme.muted, fontSize: 12 }}>
                        Reminder: {event.notify_before || 0} min before
                      </div>
                    </div>
                  ))
                )}
              </Card.Body>
            </Card>

            <Card
              className="shadow-sm"
              style={{
                background: theme.cardBg,
                borderColor: theme.border,
                borderRadius: 18,
              }}
            >
              <Card.Body>
                <h5 style={{ fontWeight: 900, color: theme.text }}>
                  Quick Tips
                </h5>
                <div style={{ color: theme.muted, fontWeight: 600, lineHeight: 1.6 }}>
                  Click any day to add an event. Busy days show a preview and
                  a “+ more” count so the calendar stays clean.
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          size="lg"
          centered
        >
          <Modal.Header
            closeButton
            style={{
              background: theme.cardBg,
              borderColor: theme.border,
              color: theme.text,
            }}
          >
            <Modal.Title style={{ fontWeight: 900 }}>
              {formatFullDate(selectedDate)}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body
            style={{
              background: theme.pageBg,
              color: theme.text,
            }}
          >
            <Row className="g-3">
              <Col md={6}>
                <Card
                  style={{
                    background: theme.cardBg,
                    borderColor: theme.border,
                    borderRadius: 16,
                  }}
                >
                  <Card.Body>
                    <h5 style={{ fontWeight: 900, color: theme.text }}>
                      Events This Day
                    </h5>

                    {selectedDayEvents.length === 0 ? (
                      <div
                        style={{
                          background: theme.cardBgSoft,
                          border: `1px solid ${theme.border}`,
                          borderRadius: 12,
                          padding: 14,
                          color: theme.muted,
                          fontWeight: 600,
                        }}
                      >
                        No events yet. Add one on the right.
                      </div>
                    ) : (
                      selectedDayEvents.map((event) => (
                        <div
                          key={`${event.id}-${event.start_time}`}
                          style={{
                            background: theme.cardBgSoft,
                            border: `1px solid ${theme.border}`,
                            borderRadius: 12,
                            padding: 12,
                            marginTop: 10,
                          }}
                        >
                          <div className="d-flex justify-content-between gap-2">
                            <div>
                              <div style={{ color: '#60a5fa', fontWeight: 900 }}>
                                {formatEastern(new Date(event.start_time))}
                              </div>
                              <div style={{ color: theme.text, fontWeight: 900 }}>
                                {event.title}
                                {event.recurrence_type &&
                                event.recurrence_type !== 'none'
                                  ? ' 🔁'
                                  : ''}
                              </div>
                              <div style={{ color: theme.muted, fontSize: 12 }}>
                                Reminder: {event.notify_before || 0} min before
                                {event.recurrence_type &&
                                event.recurrence_type !== 'none'
                                  ? ` · Repeats ${event.recurrence_type}`
                                  : ''}
                              </div>
                            </div>

                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(event)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card
                  style={{
                    background: theme.cardBg,
                    borderColor: theme.border,
                    borderRadius: 16,
                  }}
                >
                  <Card.Body>
                    <h5 style={{ fontWeight: 900, color: theme.text }}>
                      Add Event
                    </h5>

                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: theme.muted, fontWeight: 800 }}>
                        Event Title
                      </Form.Label>
                      <Form.Control
                        placeholder="Example: Go live, ship orders, restock..."
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        style={{
                          background: isDark ? '#0F172A' : '#fff',
                          color: theme.text,
                          borderColor: theme.border,
                        }}
                      />
                    </Form.Group>

                    <div
                      style={{
                        background: theme.cardBgSoft,
                        border: `1px solid ${theme.border}`,
                        borderRadius: 14,
                        padding: 12,
                        marginBottom: 16,
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div style={{ fontWeight: 900, color: theme.text }}>
                          Time
                        </div>
                        <Badge bg="primary" style={{ fontSize: 13 }}>
                          {selectedTimeLabel()}
                        </Badge>
                      </div>

                      <div style={{ color: theme.muted, fontWeight: 800, marginBottom: 6 }}>
                        Hour
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          overflowX: 'auto',
                          gap: 8,
                          paddingBottom: 8,
                        }}
                      >
                        {hours.map((hour) => (
                          <button
                            key={hour}
                            type="button"
                            onClick={() => setSelectedHour(hour)}
                            style={{
                              minWidth: 48,
                              borderRadius: 999,
                              padding: '8px 12px',
                              border: `1px solid ${
                                selectedHour === hour ? '#60a5fa' : theme.border
                              }`,
                              background:
                                selectedHour === hour ? theme.blue : theme.cardBg,
                              color: selectedHour === hour ? '#fff' : theme.text,
                              fontWeight: 900,
                            }}
                          >
                            {hour}
                          </button>
                        ))}
                      </div>

                      <div style={{ color: theme.muted, fontWeight: 800, marginBottom: 6 }}>
                        Minute
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          overflowX: 'auto',
                          gap: 8,
                          paddingBottom: 8,
                        }}
                      >
                        {minutes.map((minute) => (
                          <button
                            key={minute}
                            type="button"
                            onClick={() => setSelectedMinute(minute)}
                            style={{
                              minWidth: 52,
                              borderRadius: 999,
                              padding: '8px 12px',
                              border: `1px solid ${
                                selectedMinute === minute ? '#60a5fa' : theme.border
                              }`,
                              background:
                                selectedMinute === minute
                                  ? theme.blue
                                  : theme.cardBg,
                              color:
                                selectedMinute === minute ? '#fff' : theme.text,
                              fontWeight: 900,
                            }}
                          >
                            {String(minute).padStart(2, '0')}
                          </button>
                        ))}
                      </div>

                      <div className="d-flex gap-2 mt-2">
                        {['AM', 'PM'].map((period) => (
                          <Button
                            key={period}
                            variant={
                              selectedPeriod === period ? 'success' : 'outline-secondary'
                            }
                            className="flex-fill"
                            onClick={() => setSelectedPeriod(period)}
                          >
                            {period}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: theme.muted, fontWeight: 800 }}>
                        Reminder
                      </Form.Label>
                      <div className="d-flex flex-wrap gap-2">
                        {reminderOptions.map((option) => (
                          <Button
                            key={option.value}
                            size="sm"
                            variant={
                              Number(notifyBefore) === option.value
                                ? 'primary'
                                : 'outline-primary'
                            }
                            onClick={() => setNotifyBefore(option.value)}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: theme.muted, fontWeight: 800 }}>
                        Repeat
                      </Form.Label>
                      <div className="d-flex flex-wrap gap-2">
                        {repeatOptions.map((option) => (
                          <Button
                            key={option.value}
                            size="sm"
                            variant={
                              recurrence === option.value
                                ? 'primary'
                                : 'outline-primary'
                            }
                            onClick={() => setRecurrence(option.value)}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    </Form.Group>

                    <Button
                      variant="success"
                      className="w-100"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        'Add Event'
                      )}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer
            style={{
              background: theme.cardBg,
              borderColor: theme.border,
            }}
          >
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}

export default CalendarSettings;